import {responseStatus, serverMessage} from "utils/ErrorUtils";
import {ImageCompressionError} from "core/images/imageCompression";
import {apiErrorIdOf, SPONSOR_TOO_MANY_OPEN_SUBMISSIONS_ERROR_ID} from "api/apiErrorIds";
import type {sponsorEn} from "../i18n/en/sponsor";
import {isPaidState, type MyBookingRow} from "./myBookings";

/**
 * What to tell a sponsor — or an operator — when a sponsor request is rejected (api#420 / ui#496).
 * Plain TypeScript, importing NO React: "which sentence does a 409 mean" is a rule, and the same rule is
 * needed by the booking hook, the upload path and the admin queue, so it lives in one tested place
 * rather than as three `catch` blocks that drift.
 *
 * Same shape as `support/supportRequestFailure.ts` and `download/downloadErrors.ts`: a rejected request
 * in, a catalog KEY out — never translated copy, so adding a locale never touches this file.
 */

/**
 * The keys these mappers may return, derived FROM the catalog rather than retyped. A key that loses its
 * copy — or copy that loses its key — is then a compile error instead of a blank line in the UI.
 */
export type SponsorErrorKey = keyof (typeof sponsorEn)["sponsor"]["errors"];
export type SponsorAdminErrorKey = keyof (typeof sponsorEn)["sponsorAdmin"]["errors"];

/**
 * Classifies a rejected `POST /sponsor/bookings`.
 *
 * The two statuses that get their own sentence are the two whose remedy differs: a 409 means the
 * submission was refused by something about the world rather than about the form, a 404 means this
 * brand does not sell sponsorships at all and no amount of retrying will change that. Everything
 * else. Including the 429 abuse budget. Is the generic retry, because telling a stranger about our
 * rate limiter reveals more than it helps.
 */
export function bookingErrorKey(error: unknown): SponsorErrorKey {
	switch (responseStatus(error)) {
		case 409:
			return submissionConflictKey(error);
		case 404:
			return "unavailable";
		case 400:
			return creativeRefusalKey(serverMessage(error) ?? "");
		default:
			return "generic";
	}
}

/**
 * Which of the two 409s a submission earned (kitecorp/transferify-api-service#1362).
 *
 * Submitting became free, so an account may now hold only so many undecided bookings, and that refusal
 * arrives with the SAME status as the dates clash. Their remedies are opposites: pick another period,
 * versus wait for a decision on what you already sent. Showing "those dates are taken" to somebody
 * whose dates are fine would send them round the calendar for nothing.
 *
 * Told apart by the api's stable `ApiError.errorId`, never by its prose. That is the rule this repo
 * branches under (kitecorp/transferify-ui#1664, `api/apiErrorIds`): the api localizes every message
 * before writing it, so a sentence differs per locale and moves on every copy edit. The rest of this
 * module still reads prose, and each of those places says why it has to; this one does not have to.
 *
 * NO PROSE FALLBACK, and that is safe rather than optimistic: the cap refusal only exists on the api
 * that carries the id, so there is no deploy window in which a capped submission arrives without one.
 * An unrecognised 409 therefore stays the dates clash, which is the pre-existing answer and the one
 * carrying a remedy the advertiser can act on.
 */
function submissionConflictKey(error: unknown): SponsorErrorKey {
	return apiErrorIdOf(error) === SPONSOR_TOO_MANY_OPEN_SUBMISSIONS_ERROR_ID
		? "tooManyOpenSubmissions"
		: "datesUnavailable";
}

/**
 * Classifies a rejected `POST /sponsor/bookings/{id}/checkout`
 * (kitecorp/transferify-api-service#1362).
 *
 * The api answers ONE code, `SPONSOR_NOT_PAYABLE`, for every reason a booking cannot be paid for,
 * deliberately, because the distinction is written on the booking rather than on the response. So this
 * reads the ROW, and the row must be the one a re-read just returned: the stale row the button was
 * rendered from is by definition the one that disagreed with the server.
 *
 * Only two refusals are actually reachable from a button that appears on AWAITING_PAYMENT alone: the
 * payment landed first, or the window closed. Everything else. A vanished booking, a state this
 * transition cannot produce, a 404 for somebody else's id. Gets the generic sentence rather than a
 * guess dressed up as a diagnosis.
 */
export function checkoutRefusalKey(error: unknown, booking: MyBookingRow | null): SponsorErrorKey {
	if (responseStatus(error) !== 409 || booking === null) {
		return "generic";
	}
	if (isPaidState(booking.state)) {
		return "payAlreadyPaid";
	}
	// EXPIRED is the settled answer; an AWAITING_PAYMENT row that is no longer payable is the same
	// booking a quarter of an hour before the expiry sweep reaches it, and means the same thing.
	if (booking.state === "EXPIRED" || (booking.state === "AWAITING_PAYMENT" && !booking.payable)) {
		return "payWindowClosed";
	}
	return "generic";
}

/**
 * Which creative a 400 on `POST /sponsor/bookings` is about, from the api's own sentence.
 *
 * The only 400 a filled-in form can still earn is the creative one — every other bound is checked
 * client-side before the request goes out. It is NOT a rare race: an unclaimed upload is swept after
 * `sponsor.unclaimed-asset-ttl` (24h), so a form left open overnight submits ids that no longer exist.
 * "Something went wrong, try again" makes that a dead end, because the file is still on screen in its
 * slot and every retry fails identically — so each refusal gets copy naming the ONE file to add again
 * (api#502 did this for images; kitecorp/transferify-ui#1867 for the three single-file roles, which the
 * video treatment made reachable).
 *
 * Matched on the api's own wording in both locales, the idiom {@link uploadErrorKey} uses, because
 * `ApiError` serializes the HTTP status and never the `ErrorCode` key.
 *
 * ORDER IS THE WHOLE CORRECTNESS OF THIS, because these sentences share words:
 *
 *  1. "remove them and try again" is the one refusal about a role that is NOT ALLOWED, and it names a
 *     video and a poster while meaning the opposite of the two below. It has to be excluded first.
 *  2. the POSTER's refusal names a video too ("to show while your video loads" / "cât timp se încarcă
 *     videoclipul"), so it has to be read before the video's or it is answered as a missing video.
 *  3. only then the video, the logo and the images, which share nothing.
 */
function creativeRefusalKey(message: string): SponsorErrorKey {
	if (/remove them|elimină-le/i.test(message)) {
		return "assetsNotAllowed";
	}
	if (/poster|copert/i.test(message)) {
		return "posterUnavailable";
	}
	if (/video/i.test(message)) {
		return "videoUnavailable";
	}
	if (/logo/i.test(message)) {
		return "logoUnavailable";
	}
	if (/images|imagini/i.test(message)) {
		return "assetsUnavailable";
	}
	return "generic";
}

/**
 * Whether a rejected submission means our copy of the calendar is out of date.
 *
 * Here rather than in the hook because it is a RULE about what a refusal means, which is this module's
 * whole reason to exist — the hook is a thin adapter by mandate (epic #26), and a rule spelled out in a
 * `catch` block is the one that drifts from the sentence it is supposed to agree with.
 *
 * Only the calendar clash qualifies. A 404 says this brand sells no sponsorships at all, the
 * open-submission cap is about how many decisions are outstanding rather than about any date, and a
 * creative or generic failure says nothing about which days are free. Re-reading the calendar for any
 * of them would spend a request to learn nothing.
 */
export function invalidatesCalendar(key: SponsorErrorKey): boolean {
	return key === "datesUnavailable";
}

/**
 * Which control a file was picked in, and therefore which vocabulary its refusal is written in
 * (kitecorp/transferify-ui#1867).
 *
 * `image` is the multi-file creative picker; the other three are the single-file slots. The names are
 * the form's, not the wire's — `SponsorApi.SponsorAssetRole` is the wire's, and the two are kept apart
 * for the reason `ROLE_ON_THE_WIRE` in `useSponsorBooking.ts` gives.
 */
export type SponsorUploadRole = "image" | "logo" | "video" | "poster";

/**
 * Classifies a rejected `POST /sponsor/assets`.
 *
 * Every upload goes to one endpoint and every refusal is a bare 400 carrying a localized sentence:
 * `ApiError` puts the HTTP status in `code` and never serializes the `ErrorCode` key, so the only
 * discriminator is the api's own prose, matched here on the words it is built from in each locale.
 *
 * THE ROLE IS PART OF THE READ, and not a convenience. "MB" appears in a picture's size refusal and in
 * a video's, three identical characters carrying different verdicts, so without it a rejected video
 * earned "That image's dimensions are too large. Each side must be under 4,000 pixels." — advice about
 * a picture, quoting a bound the video never broke. It also decides which CONTROL the sentence is being
 * shown against: a mark refused for its format has to say so about a mark, because the message appears
 * under the mark's own picker.
 *
 * Anything unrecognised falls back per role: a 400 on a video is always a verdict about that FILE, so
 * it says the video could not be read; anything else is the generic "try another".
 */
export function uploadErrorKey(error: unknown, role: SponsorUploadRole = "image"): SponsorErrorKey {
	if (responseStatus(error) !== 400) {
		// Not a verdict about the file at all: a throttle, a 5xx or a dropped connection.
		return "uploadFailed";
	}
	const message = serverMessage(error) ?? "";
	if (role === "video") {
		// Container names first: they appear ONLY in the unsupported-format message, in both locales
		// ("an MP4 (H.264) or WebM file" / "un fișier MP4 (H.264) sau WebM").
		if (/mp4|webm/i.test(message)) {
			return "videoUnsupported";
		}
		// Then the duration, before the size: only the too-long message names seconds, and only the
		// too-large one names MB, so neither can be read as the other.
		if (/second|secund/i.test(message)) {
			return "videoTooLong";
		}
		if (/\bmb\b/i.test(message)) {
			return "videoTooLarge";
		}
		/*
		 * The unreadable DURATION, matched explicitly.
		 *
		 * It used to reach its key through the fallback below, which is why splitting that fallback off
		 * turned this case red rather than leaving it green — the verdict was never recognised, only
		 * caught. Both locales name the duration where nothing else surviving to here does: "how long
		 * this video plays" / "cât durează videoclipul". The too-long message names a duration too, but
		 * it is answered above by its own seconds.
		 */
		if (/how long|durează/i.test(message)) {
			return "videoUnreadable";
		}
		/*
		 * A refusal this build cannot place. It gets a sentence that says only that, and NOT
		 * `videoUnreadable`, which diagnoses a fragmented export and prescribes a re-encode: an
		 * unrecognised 400 is no evidence for either, and telling an advertiser their finished file is a
		 * live recording would be a guess shown as a fact. It still names the video, because a 400 on an
		 * upload is a verdict about that file — the transport failures are `uploadFailed`, above.
		 */
		return "videoRefused";
	}
	// Format names first: they appear ONLY in the unsupported-type messages, in both locales.
	if (/png|jpe?g|webp/i.test(message)) {
		return role === "logo" ? "logoUnsupported" : (role === "poster" ? "posterUnsupported" : "uploadUnsupported");
	}
	// The size message is the one that quotes a byte ceiling and a pixel edge ("under 1 MB and at most
	// 4000 pixels on each side" / "sub 1 MB și de cel mult 4000 de pixeli pe fiecare latură"). A poster
	// is stored and bounded exactly like a background image, so it shares that verdict.
	if (/\bmb\b|pixel/i.test(message)) {
		return "uploadTooLarge";
	}
	return "uploadFailed";
}

/**
 * A file THIS CLIENT refused, carrying the sentence it was refused with
 * (kitecorp/transferify-ui#2143).
 *
 * It exists because a multi-file pick reports per file. `processPickedBatch` never rejects - it
 * collects each file's error beside the file - so a refusal decided before any upload has to travel
 * out of the step as a thrown value, and it must carry its own verdict: a bare `Error` reaching
 * {@link pickedImageErrorKey} would be read as an api failure and reported as "try another file",
 * which is advice about the network for a picture that is simply the wrong shape.
 */
export class PickedImageRefused extends Error {
	constructor(readonly key: SponsorErrorKey) {
		super(key);
		this.name = "PickedImageRefused";
	}
}

/**
 * Which sentence ONE file in a multi-file creative pick earned.
 *
 * Three sources, in the order they can be told apart: a refusal this client made before uploading, a
 * picture the browser could not decode or re-encode, and the api's own verdict on one it did upload.
 * The api's is read in the IMAGE picker's vocabulary - named rather than defaulted, because the role
 * decides which prose is matched and a default relied upon is one that changes silently.
 *
 * Here rather than in the hook because it is a rule about what a failure MEANS, which is this module's
 * whole reason to exist; it was two branches inline in `useSponsorBooking` until this ticket, and the
 * third could not have been added there without the hook holding a verdict of its own.
 */
export function pickedImageErrorKey(error: unknown): SponsorErrorKey {
	if (error instanceof PickedImageRefused) {
		return error.key;
	}
	if (error instanceof ImageCompressionError) {
		return "uploadUnprocessable";
	}
	return uploadErrorKey(error, "image");
}

/**
 * Classifies a rejected `GET /sponsor/bookings` (epic #441).
 *
 * A 401 maps to NO message at all. The advertiser's own list is not something that failed then — it is
 * something that does not apply, because the session is not (or is no longer) authenticated. Rendering
 * "something went wrong" would blame the product for a signed-out browser, and the page's own sign-in
 * routing is already the remedy. Everything else is the generic retry.
 */
export function myBookingsErrorKey(error: unknown): SponsorErrorKey | null {
	return responseStatus(error) === 401 ? null : "generic";
}

/**
 * Classifies a rejected approve/reject in the operator queue.
 *
 * A 409 is either "somebody already reviewed this" or "the dates are now held by another approved
 * booking", and the operator's next move genuinely differs — refresh the queue versus pick a different
 * booking — so the two are told apart. As above, the api leaves only the localized message to go on;
 * "reviewed" / "analizat" appear in exactly one of the two messages, and the Romanian stem "rezerv"
 * deliberately is NOT used because it appears in BOTH ("Perioada … rezervată" and "Această rezervare …").
 */
export function adminErrorKey(error: unknown): SponsorAdminErrorKey {
	if (responseStatus(error) !== 409) {
		return "generic";
	}
	return /reviewed|analizat/i.test(serverMessage(error) ?? "") ? "alreadyReviewed" : "conflict";
}
